import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { supabase } from './supabase';
import { useAuth } from './auth';
import { useHousehold, useIsAdmin, useMyInstructing } from './queries';
import { shouldSearch, type SearchResult, type Viewer } from './searchResults';

export * from './searchResults';

export function useDebounced<T>(value: T, ms = 150): T {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

export function useViewer(): Viewer {
  const admin = useIsAdmin();
  const instructing = useMyInstructing();
  const household = useHousehold();
  return {
    isStaff: !!admin.data,
    instructorDojoIds: (instructing.data ?? []).map(i => i.dojo?.id).filter((x): x is string => !!x),
    householdId: household.data?.householdId ?? null,
  };
}

export function useSearch(q: string, opts: { limit?: number; perKind?: number } = {}) {
  const { session } = useAuth();
  const term = q.trim();
  const limit = opts.limit ?? 25, perKind = opts.perKind ?? 5;
  return useQuery({
    queryKey: ['search', term, limit, perKind],
    enabled: !!session && shouldSearch(term),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('search', { p_q: term, p_limit: limit, p_per_kind: perKind });
      if (error) throw error;
      return (data ?? []) as SearchResult[];
    },
  });
}
